import { useAfterHydration } from "../../../common/helpers/afterHydration";
import React, { useState } from "react";
import { FormattedMessage,  useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Seo from "../../../common/helpers/seo";
import { rhytNameTask } from "../../../common/sampleTasks";

import { amazonHost } from "../../paths";

import { IStateTree } from "../../store/interfaces";
import { IUser, RhythmSyllableType } from "../../store/user/models";
import RhytLadder from "../../components/Task/types/RhytName/RhytLadder";
import Tabs from "../../components/Tabs";
import { Checkbox } from "../../components/Form";
import RhytName from "../../components/Task/types/RhytName";
import { ICheckboxEvent } from "../../types";

import RhytLadderArticle from "../rhytladder";
import { P2, StyledArticle } from "../styles";

import Hello from "./Hello";

import { StyledSpring, Description, Content, Row, TaskWrap, RhytLadderWrapper, Container, Screen } from "./styles";
import messages from "./messages";
import { IProps, IRhytNameTypes } from "./interfaces";
import { IPlan, ITask } from "../../../common/types";
import FormattedHtmlMessage from "../../../common/helpers/FormattedHtmlMessage";

const simpleTypes: IRhytNameTypes = {
    ta: true,
    ti: true,
    ri: true,
};

const advancedTypes: IRhytNameTypes = {
    ta: true,
    ti: true,
    ri: true,
    tim: true,
    tam: true,
    taa: true,
    taaa: true,
    taaaa: true,
};

const customTypes: IRhytNameTypes = {
    ta: true,
    ti: true,
    taa: true,
    tam: true,
};

const RhytNameArticle = ({
    light,
    noTitle,
}: IProps) => {
    const { formatMessage, locale } = useIntl();
    const user = useSelector((state: IStateTree) => state.user as IUser);
    const webp = useSelector((state: IStateTree) => state.webp);
    const [rhytNameTypes, setRhytNameTypes] = useState<IRhytNameTypes>(simpleTypes);
    // The exercise widgets measure the window, so they cannot be part of the first render.
    const afterHydration = useAfterHydration();
    const changeTab = (level: number) => {
        if (level === 0) {
            setRhytNameTypes(simpleTypes);
        } else if (level === 1) {
            setRhytNameTypes(advancedTypes);
        } else if (level === 2) {
            setRhytNameTypes(customTypes);
        }
    };
    const changeRhythmName = (e: ICheckboxEvent, name: string) => {
        customTypes[name] = e.target.checked;
        setRhytNameTypes({ ...customTypes });
    };
    return (
        <StyledSpring withShadow marginTop={50}>
            <StyledArticle light={light}>
                <Seo
                    title={formatMessage(messages.title)}
                    type={"article"}
                    description={formatMessage(messages.description)}
                />
                <Hello noTitle={noTitle} />
                <Container
                    style={{
                        backgroundImage:
                            "url(" + amazonHost + `landing/smartboard.${webp ? "webp" : "png"})`,
                        paddingBottom: 38,
                    }}
                >
                    <Screen>
                        <TaskWrap>
                            {afterHydration && (
                                <RhytName
                                    task={rhytNameTask}
                                    playPlan
                                    completed={() => null}
                                    setReset={() => null}
                                    saveState={() => null}
                                    restoreState={() => null}
                                    showSolution={() => null}
                                    plan={{} as IPlan}
                                />
                            )}
                        </TaskWrap>
                    </Screen>
                </Container>
                <RhytLadderArticle />
                <RhytLadderWrapper>
                    {afterHydration ? (
                        <RhytLadder
                            handleInput={() => { }}
                            frameScale={window.innerWidth < 768 ? 0.55 : 1}
                            rhytNameTypes={rhytNameTypes}
                            isTiriTaiTi={user.rhythmSyllableType === RhythmSyllableType.KodalyTiRiTaiTi}
                            locale={locale}
                            isTiriTamTi={user.rhythmSyllableType === RhythmSyllableType.KodalyTiRi}
                            isLow={false}
                            isTeteTamTi={user.rhythmSyllableType === RhythmSyllableType.KodalyTeTe}
                            isRiRiTaiTi={user.rhythmSyllableType === RhythmSyllableType.KodalyRiRiTaiTi}
                        />
                    ) : null}
                </RhytLadderWrapper>
                <Description>
                    <Tabs onChange={changeTab} lg autoPlay autoPlayTime={4000}>
                        {[
                            {
                                title: <FormattedMessage {...messages.simpleTitle} />,
                                content: (
                                    <Content>
                                        <P2>
                                            <FormattedMessage {...messages.simple} />
                                        </P2>
                                    </Content>
                                ),
                            },
                            {
                                title: <FormattedHtmlMessage {...messages.combinedTitle} />,
                                content: (
                                    <Content>
                                        <P2>
                                            <FormattedHtmlMessage {...messages.combined} />
                                        </P2>
                                    </Content>
                                ),
                            },
                            {
                                title: <FormattedMessage {...messages.customTitle} />,
                                content: (
                                    <Content>
                                        <P2>
                                            <FormattedMessage {...messages.custom} />
                                        </P2>
                                        <P2 as="div">
                                            <Row>
                                                <Checkbox
                                                    checked={rhytNameTypes.ri}
                                                    label={<b>{formatMessage(messages.ri).replace(
                                                        user.rhythmSyllableType === RhythmSyllableType.KodalyTeTe ? "(ri)" : "none",
                                                        "(te)").replace("<b>", "").replace("</b>", "")}</b>}
                                                    onChange={e => changeRhythmName(e, "ri")}
                                                />
                                            </Row>
                                            <Row>
                                                <Checkbox
                                                    checked={rhytNameTypes.ti}
                                                    label={<FormattedHtmlMessage {...messages.ti} />}
                                                    onChange={e => changeRhythmName(e, "ti")}
                                                />
                                                <Checkbox
                                                    checked={rhytNameTypes.tim}
                                                    label={<FormattedHtmlMessage {...messages.tim} />}
                                                    onChange={e => changeRhythmName(e, "tim")}
                                                />
                                            </Row>
                                            <Row>
                                                <Checkbox
                                                    checked={rhytNameTypes.ta}
                                                    label={<FormattedHtmlMessage {...messages.ta} />}
                                                    onChange={e => changeRhythmName(e, "ta")}
                                                />
                                                <Checkbox
                                                    checked={rhytNameTypes.tam}
                                                    label={formatMessage(messages.tam).replace(
                                                        user.rhythmSyllableType === "tiriTaiTi" ? "m)" : "none",
                                                        "i)"
                                                    )}
                                                    onChange={e => changeRhythmName(e, "tam")}
                                                />
                                            </Row>
                                            <Row>
                                                <Checkbox
                                                    checked={rhytNameTypes.taa}
                                                    label={<FormattedHtmlMessage {...messages.taa} />}
                                                    onChange={e => changeRhythmName(e, "taa")}
                                                />
                                                <Checkbox
                                                    checked={rhytNameTypes.taaa}
                                                    label={<FormattedHtmlMessage {...messages.taaa} />}
                                                    onChange={e => changeRhythmName(e, "taaa")}
                                                />
                                            </Row>
                                            <Row>
                                                <Checkbox
                                                    checked={rhytNameTypes.taaaa}
                                                    label={<FormattedHtmlMessage {...messages.taaaa} />}
                                                    onChange={e => changeRhythmName(e, "taaaa")}
                                                />
                                            </Row>
                                        </P2>
                                    </Content>
                                ),
                            },
                        ]}
                    </Tabs>
                </Description>
            </StyledArticle>
        </StyledSpring>
    );
};

export default RhytNameArticle;
